import React, { useState } from 'react';
import { Building2, Home, Users, GraduationCap, Building, ChevronDown, Check, Info } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const PROPERTY_TYPES = [
  {
    value: 'standard',
    label: 'Standard Let',
    description: 'Single household tenancy - one family or individual',
    icon: Home,
    features: ['Single tenancy agreement', 'Standard compliance', 'Simple rent collection'],
    sharedOccupancy: false
  },
  {
    value: 'hmo',
    label: 'HMO',
    description: 'House in Multiple Occupation - multiple tenants sharing',
    icon: Users,
    features: ['Room-by-room letting', 'HMO licensing', 'Multiple tenancies', 'Communal area management'],
    sharedOccupancy: true
  },
  {
    value: 'student_let',
    label: 'Student Let',
    description: 'Shared house for students - group or individual letting',
    icon: GraduationCap,
    features: ['Group applications', 'Guarantor workflow', 'Academic year aligned', 'May trigger HMO rules'],
    sharedOccupancy: true
  },
  {
    value: 'pbsa',
    label: 'PBSA / Student Halls',
    description: 'Purpose-Built Student Accommodation',
    icon: Building,
    features: ['Unit/room inventory', 'Building-level compliance', 'Code membership tracking', 'Bulk operations'],
    sharedOccupancy: true
  }
];

export function PropertyTypeSelector({ value, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const selected = PROPERTY_TYPES.find(t => t.value === value) || PROPERTY_TYPES[0];
  const Icon = selected.icon;
  
  return (
    <div className="relative">
      <label className="block text-sm font-medium text-slate-700 mb-2">
        Property Type
      </label>
      
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between p-4 border rounded-lg transition-all ${
          disabled ? 'bg-slate-100 cursor-not-allowed' : 'bg-white hover:border-primary cursor-pointer'
        } ${isOpen ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200'}`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${selected.sharedOccupancy ? 'bg-purple-100' : 'bg-blue-100'}`}>
            <Icon className={`w-5 h-5 ${selected.sharedOccupancy ? 'text-purple-600' : 'text-blue-600'}`} />
          </div>
          <div className="text-left">
            <div className="font-medium">{selected.label}</div>
            <div className="text-sm text-slate-500">{selected.description}</div>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-lg shadow-xl">
          {PROPERTY_TYPES.map(type => {
            const TypeIcon = type.icon;
            const isSelected = type.value === value;
            
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => {
                  onChange(type.value);
                  setIsOpen(false);
                }}
                className={`w-full p-4 text-left hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                  isSelected ? 'bg-primary/5' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${type.sharedOccupancy ? 'bg-purple-100' : 'bg-blue-100'}`}>
                    <TypeIcon className={`w-5 h-5 ${type.sharedOccupancy ? 'text-purple-600' : 'text-blue-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{type.label}</span>
                      {isSelected && <Check className="w-5 h-5 text-primary" />}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">{type.description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {type.features.map((f, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      
      {selected.sharedOccupancy && (
        <div className="mt-2 p-3 bg-purple-50 rounded-lg flex items-start gap-2">
          <Info className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-purple-700">
            Shared occupancy properties support multiple tenancies, room management, and communal area tracking.
          </p>
        </div>
      )}
    </div>
  );
}

export function PropertyTypeBadge({ type }) {
  const typeInfo = PROPERTY_TYPES.find(t => t.value === type);
  
  if (!typeInfo) return null;
  
  const Icon = typeInfo.icon;
  const colors = typeInfo.sharedOccupancy 
    ? 'bg-purple-100 text-purple-700' 
    : 'bg-blue-100 text-blue-700';
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colors}`}>
      <Icon className="w-3.5 h-3.5" />
      {typeInfo.label}
    </span>
  );
}

export default PropertyTypeSelector;
